/**
 * queue #696 — 마크다운 heading 앵커 조회 SSOT.
 *
 * `MarkdownView` 의 `rehype-sanitize` 는 hast-util-sanitize 기본 `clobberPrefix: 'user-content-'`
 * 를 그대로 쓰므로, 렌더된 heading 의 DOM `id` 는 `user-content-<slug>` 다. 반면 목차·검색·
 * 해시 딥링크가 들고 있는 슬러그는 접두사 없는 raw slug(`parseStandardsHeadings` · GithubSlugger ·
 * BE 슬러거 산출) 라, `getElementById(rawSlug)` 는 대상을 못 찾고 스크롤이 조용히 no-op 이 된다
 * (#688/#689 딥링크 무동작의 근본 원인 — 2026-07-14 발견).
 *
 * 조회 순서: ① raw id(접두사 미적용 렌더러 대비) ② `user-content-<slug>` ③ autolink 앵커
 * (`a[href="#<slug>"]`) 의 부모 heading — href 는 clobber 대상이 아니라 원본 슬러그를 유지한다.
 */

/** hast-util-sanitize 기본 clobber 접두사 */
export const MARKDOWN_ID_CLOBBER_PREFIX = 'user-content-';

/**
 * `slug`(접두사 없는 raw slug) 에 대응하는 heading 엘리먼트를 찾는다.
 *
 * @param slug 앵커 슬러그(선행 `#` 허용)
 * @param scope 조회 범위(기본 document) — 모달/컨테이너 스코프로 좁혀 id 충돌을 피한다
 * @returns 대상 엘리먼트 또는 null
 */
export function findAnchorTarget(
  slug: string | null | undefined,
  scope?: ParentNode | null,
): HTMLElement | null {
  if (!slug) return null;
  const trimmed = slug.startsWith('#') ? slug.slice(1) : slug;
  if (!trimmed) return null;

  // location.hash 는 한글 슬러그를 퍼센트 인코딩해 돌려준다(%EA%B0%9C…) — heading id 는 디코딩된
  // 원문이라 인코딩 상태로 조회하면 영구 미스(2026-07-14 라이브 실측). 디코딩 실패는 원문 유지.
  let raw = trimmed;
  try {
    raw = decodeURIComponent(trimmed);
  } catch {
    raw = trimmed;
  }

  const root: ParentNode = scope ?? document;

  const byRawId = root.querySelector<HTMLElement>(`[id="${CSS.escape(raw)}"]`);
  if (byRawId) return byRawId;

  const byPrefixed = root.querySelector<HTMLElement>(
    `[id="${CSS.escape(MARKDOWN_ID_CLOBBER_PREFIX + raw)}"]`,
  );
  if (byPrefixed) return byPrefixed;

  const autolink = root.querySelector<HTMLAnchorElement>(`a[href="#${CSS.escape(raw)}"]`);
  const heading = autolink?.closest('h1, h2, h3, h4, h5, h6');
  return (heading as HTMLElement | null) ?? null;
}
